import type { RiskModel } from "@repo/shared/types";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { DocButton } from "@/components/ui/doc-button";
import { useRiskModels } from "@/lib/hooks/use-risk-models";
import { useT } from "@/lib/i18n/use-t";
import { CloneRiskModelDialog } from "./clone-risk-model-dialog";
import { RiskModelDialog } from "./risk-model-dialog";
import { RiskModelsLoader } from "./risk-models-loader";
import { RiskModelsTable } from "./risk-models-table";
import { T } from "./risk-models-wrapper.i18n";

export function RiskModelsWrapper() {
  const t = useT(T);
  const { models, isLoading } = useRiskModels();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<any | null>(null);
  // QL-212 — the duplicate action: the copy starts inactive, so its input
  // scopes stay editable even when the source is locked by being active.
  const [cloningModel, setCloningModel] = useState<RiskModel | null>(null);

  const handleEdit = (model: any) => {
    setEditingModel(model);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingModel(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.riskModels }]}
        title={t.title}
        description={t.subtitle}
        actions={
          <DocButton docSlug="risk-models" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={() => setDialogOpen(true)}>{t.createRiskModel}</Button>
        </div>

        {isLoading && models.length === 0 ? (
          <RiskModelsLoader />
        ) : (
          <RiskModelsTable models={models} onEdit={handleEdit} onClone={setCloningModel} />
        )}

        <RiskModelDialog open={dialogOpen} model={editingModel} onClose={handleClose} />

        <CloneRiskModelDialog
          open={!!cloningModel}
          model={cloningModel}
          onClose={() => setCloningModel(null)}
        />
      </div>
    </div>
  );
}
